import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { FaTimes } from "react-icons/fa";

const REASONS = [
  { value: "expiry", label: "Expired" },
  { value: "return", label: "Returned to Supplier" },
  { value: "broken", label: "Broken / Damaged" },
  { value: "other", label: "Other" },
];

const AdjustmentDrawer = ({ isOpen, onClose, productId, productName,inventoryId, onSaved }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Return-specific state
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [matchedItems, setMatchedItems] = useState([]); // one entry per purchase-bill line item
  // keyed by row key -> { checked, returnQty }
  const [rowState, setRowState] = useState({});
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  
  const [availableQty, setAvailableQty] = useState(null);
  const [loadingAvailableQty, setLoadingAvailableQty] = useState(false);

  useEffect(() => {
    if (isOpen) resetAll();
  }, [isOpen]);

  useEffect(() => {
    if (reason === "return" && suppliers.length === 0) fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);

  useEffect(() => {
    if (reason === "return" && supplierId) {
      fetchSupplierPurchaseItems(supplierId);
    } else {
      setMatchedItems([]);
      setRowState({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  useEffect(() => {
    if (isOpen && reason === "broken" && productId) {
      fetchAvailableQuantity();
    } else if (reason !== "broken") {
      setAvailableQty(null);
    }
  }, [isOpen, reason, productId]);

  const resetAll = () => {
    setReason("");
    setNote("");
    setQuantity("");
    setSupplierId("");
    setMatchedItems([]);
    setRowState({});
    setReturnDate(new Date().toISOString().slice(0, 10));
    setAvailableQty(null);
  };

  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const res = await httpService.get(`/vendorsupplier/getVendorSuppliers`);
      if (res.code === 200) setSuppliers(res.data || []);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      toast.error("Failed to load suppliers");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Pulls every purchase bill for this supplier, then flattens out the line
  // items matching this product — one entry per bill the product was bought on.
  // NOTE: assumes the list endpoint returns the same `items` shape as
  // /vendorpurchase/getVendorPurchase/:id. If the list endpoint omits `items`,
  // this needs a per-bill fetch instead.
  const fetchSupplierPurchaseItems = async (id) => {
    setLoadingItems(true);
    setMatchedItems([]);
    setRowState({});
    try {
      const res = await httpService.get(`/vendorpurchase/getVendorSupplierPurchases/${id}`);
      if (res?.status && res?.code === 200) {
        const bills = res.data || [];
        const rows = [];
        bills.forEach((bill) => {
          (bill.items || []).forEach((item) => {
            if (item.vendor_product_id === productId) {
              rows.push({
                key: `${bill._id}_${item._id}`,
                billId: bill._id,
                itemId: item._id,
                invoiceNumber: bill.invoice_number || "-",
                invoiceDate: bill.invoice_date,
                vendorProductId: item.vendor_product_id,
                productName: item.product_name || productName || "",
                sku: item.sku_number || "-",
                purchasedQty: Number(item.quantity) || 0,
                purchasePrice: Number(item.purchase_price) || 0,
                gst: Number(item.gst_slab) || 0,
                mrp: Number(item.mrp) || 0,
              });
            }
          });
        });
        // most recent bill first
        rows.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
        setMatchedItems(rows);
      }
    } catch (err) {
      console.error("Error fetching supplier purchase items:", err);
      toast.error("Failed to load purchase history for this supplier");
    } finally {
      setLoadingItems(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
  };

  const getRow = (key) => rowState[key] || { checked: false, returnQty: "" };

  const toggleRow = (key) => {
    setRowState((prev) => ({
      ...prev,
      [key]: { ...getRow(key), checked: !getRow(key).checked },
    }));
  };

  const fetchAvailableQuantity = async () => {
    setLoadingAvailableQty(true);

    try {
      const response = await httpService.get(
        `/vendorproduct/getAdjustedAvailableStocks/${productId}`
      );

      if (response?.status && response?.code === 200) {
        setAvailableQty(response.data?.quantity_available || 0);
      } else {
        setAvailableQty(0);
        toast.error(response?.message || "Failed to fetch available quantity");
      }
    } catch (err) {
      console.error("Error fetching available quantity:", err);
      toast.error("Failed to fetch available quantity");
      setAvailableQty(0);
    } finally {
      setLoadingAvailableQty(false);
    }
  };

  const handleReturnQtyChange = (row, value) => {
    const qty = value === "" ? "" : parseInt(value, 10) || 0;
    if (qty !== "" && qty > row.purchasedQty) {
      toast.error(`Cannot return more than ${row.purchasedQty} for this bill.`);
      return;
    }
    setRowState((prev) => ({
      ...prev,
      [row.key]: { ...getRow(row.key), returnQty: qty },
    }));
  };

  const rowTotal = (row) => {
    const state = getRow(row.key);
    const qty = Number(state.returnQty || 0);
    return (qty * row.purchasePrice * (1 + row.gst / 100)).toFixed(2);
  };

  const selectedRows = matchedItems.filter((row) => getRow(row.key).checked);
  const grandTotal = selectedRows
    .reduce((sum, row) => sum + Number(rowTotal(row)), 0)
    .toFixed(2);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    setSubmitting(true);
    try {
      if (reason === "return") {
        if (!supplierId) {
          toast.error("Please select a supplier");
          setSubmitting(false);
          return;
        }
        if (selectedRows.length === 0) {
          toast.error("Please check at least one purchase bill line to return");
          setSubmitting(false);
          return;
        }
        const invalidRow = selectedRows.find((row) => {
          const qty = getRow(row.key).returnQty;
          return !qty || qty <= 0;
        });
        if (invalidRow) {
          toast.error("Please enter a valid return quantity for each checked item");
          setSubmitting(false);
          return;
        }

        // NOTE: adding purchase_bill_id per item so the return is traceable
        // back to the specific invoice it came from — confirm the backend
        // accepts this field before relying on it.
        const payload = {
          supplier_id: supplierId,
          items: selectedRows.map((row) => {
            const state = getRow(row.key);
            return {
              purchase_bill_id: row.billId,
              purchase_bill_item_id: row.itemId,
              vendor_product_id: row.vendorProductId,
              sku_number: row.sku,
              return_quantity: state.returnQty,
              purchase_price: row.purchasePrice,
              gst_amount: row.gst,
              return_amount: parseFloat(rowTotal(row)),
              mrp: row.mrp,
              return_date: returnDate,
            };
          }),
        };

        const response = await httpService.post(
          "/vendorsupplierreturn/addVendorSupplierReturn",
          payload
        );

        if (response?.status) {
          toast.success("Return saved successfully!");
          onSaved?.();
          onClose();
        } else {
          toast.error(response?.message || "Failed to save return");
        }
        
        } else {
        if (!quantity || Number(quantity) <= 0) {
          toast.error("Please enter a valid quantity");
          setSubmitting(false);
          return;
        }

        if (reason === "broken" && Number(quantity) > Number(availableQty)) {
          toast.error(
            `Damaged quantity cannot be greater than available quantity (${availableQty}).`
          );
          setSubmitting(false);
          return;
        }

        const payload = {
          reason: reason === "broken" ? "damaged/broken" : reason,
          quantity: Number(quantity),
          note,
          vendor_product_id: productId,
          inventoryId,
        };

        const response = await httpService.post(
          `/vendorproduct/addAdjustment/${productId}`,
          payload
        );

        if (response?.status && response?.code === 200) {
          toast.success(response.message || "Adjustment saved successfully");
          onSaved?.();
          onClose();
        } else {
          toast.error(response?.message || "Failed to save adjustment");
        }
      }
    } catch (err) {
      console.error("Error saving adjustment:", err);
      const msg = err.response?.data?.message || err.message || "Failed to save adjustment";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Add Adjustment</h2>
            {productName && <p className="text-xs text-gray-500 mt-0.5">{productName}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500">
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Reason *</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-5"
          >
            <option value="">Select a reason</option>
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          {reason === "return" && (
            <>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Supplier *</label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
                disabled={loadingSuppliers}
              >
                <option value="">{loadingSuppliers ? "Loading suppliers..." : "Select a supplier"}</option>
                {suppliers.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}{s.contact_info?.phone ? ` (${s.contact_info.phone})` : ""}
                  </option>
                ))}
              </select>

              {loadingItems && (
                <p className="text-sm text-gray-500 mb-4">Loading purchase history...</p>
              )}

              {!loadingItems && supplierId && matchedItems.length === 0 && (
                <p className="text-sm text-red-600 mb-4">
                  No purchase bills found for this product with the selected supplier.
                </p>
              )}
               {matchedItems.map((row) => {
                const state = getRow(row.key);
                return (
                  <div key={row.key} className="mb-4">
                    <label className="flex items-center gap-2 text-xs text-gray-600 mb-1 px-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.checked}
                        onChange={() => toggleRow(row.key)}
                        className="w-4 h-4"
                      />
                      <span className="font-semibold">Invoice #:</span> {row.invoiceNumber}
                      <span className="mx-2">|</span>
                      <span className="font-semibold">Date:</span> {formatDate(row.invoiceDate)}
                    </label>
                    <div className="border rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Return Qty</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">GST</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t">
                              <td className="px-3 py-2">{row.productName}</td>
                              <td className="px-3 py-2">{row.sku}</td>
                              <td className="px-3 py-2">{row.purchasedQty}</td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={row.purchasedQty}
                                  value={state.returnQty}
                                  onChange={(e) => handleReturnQtyChange(row, e.target.value)}
                                  className="w-20 border rounded px-2 py-1"
                                />
                              </td>
                              <td className="px-3 py-2">₹{row.purchasePrice.toFixed(2)}</td>
                              <td className="px-3 py-2">{row.gst}%</td>
                              <td className="px-3 py-2 font-semibold">₹{rowTotal(row)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {matchedItems.length > 0 && (
                <>
                  {selectedRows.length > 0 && (
                    <div className="text-right text-sm font-semibold mb-4 px-1">
                      Total: ₹{grandTotal}
                    </div>
                  )}
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Return Date</label>
                  <input
                    type="date"
                    value={returnDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setReturnDate(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
                  />
                </>
              )}
            </>
          )}

         {reason === "broken" && (
  <>
    <div className="grid grid-cols-2 gap-4 mb-4">

      {/* Available Quantity */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Available Quantity
        </label>

        <input
          type="number"
          value={loadingAvailableQty ? "" : availableQty ?? ""}
          readOnly
          placeholder={loadingAvailableQty ? "Loading..." : "0"}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
        />
      </div>

      {/* Damaged Quantity */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Damaged Quantity *
        </label>

        <input
          type="number"
          min="1"
          max={availableQty || 0}
          value={quantity}
          disabled={loadingAvailableQty || !availableQty}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "") {
              setQuantity("");
              return;
            }

            const qty = Number(value);

            if (qty > availableQty) {
              toast.error(
                `Damaged quantity cannot be greater than available quantity (${availableQty}).`
              );
              return;
            }

            setQuantity(qty);
          }}
          className="w-full border rounded-lg px-3 py-2 text-sm"
          placeholder="Enter quantity"
        />
      </div>

    </div>

    <label className="block text-xs font-semibold text-gray-600 mb-1">
      Note
    </label>

    <textarea
      value={note}
      onChange={(e) => setNote(e.target.value)}
      rows={3}
      className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
      placeholder="Optional notes about this adjustment"
    />
  </>
)}

  {(reason === "expiry" || reason === "other") && (
    <>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        Quantity *
      </label>

      <input
        type="number"
        min="1"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
      />

      <label className="block text-xs font-semibold text-gray-600 mb-1">
        Note
      </label>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
        placeholder="Optional notes about this adjustment"
      />
    </>
  )}
        </div>

        {reason && (
          <div className="px-6 py-4 border-t bg-white">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium text-sm disabled:opacity-60"
            >
              {submitting ? "Saving..." : "Save Adjustment"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdjustmentDrawer;